/**
 * Centralised motion primitives — Apple/Linear/Stripe feel.
 *
 * Components:
 *   <Reveal>       — IntersectionObserver-based fade-up on scroll. Use everywhere.
 *   <CountUp>      — Numeric count animation. Used for KPI cards.
 *   <MagneticButton>— Slight magnetic follow on hover (premium feel for CTAs).
 *
 * All components respect prefers-reduced-motion (handled at CSS level).
 */
import { useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------------ */
/* Reveal — fade-up when in viewport                                   */
/* ------------------------------------------------------------------ */

export function Reveal({ as: Tag = "div", delay = 0, className = "", children, ...rest }) {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Use IO; if not supported, just show immediately.
        if (!("IntersectionObserver" in window)) {
            setShown(true);
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setShown(true);
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <Tag
            ref={ref}
            className={`bm-reveal-init ${shown ? "bm-reveal-in" : ""} ${className}`}
            style={{ transitionDelay: delay ? `${delay}ms` : undefined }}
            {...rest}
        >
            {children}
        </Tag>
    );
}

/* ------------------------------------------------------------------ */
/* CountUp — smooth easeOut count animation                            */
/* ------------------------------------------------------------------ */

export function CountUp({ to = 0, duration = 1100, prefix = "", suffix = "", decimals = 0, className = "" }) {
    const ref = useRef(null);
    const [n, setN] = useState(0);
    const [started, setStarted] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (!("IntersectionObserver" in window)) {
            setN(to);
            setStarted(true);
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !started) {
                        setStarted(true);
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.2 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [to, started]);

    useEffect(() => {
        if (!started) return;
        const start = performance.now();
        const from = 0;
        const delta = Number(to) - from;
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - t, 3);
            setN(from + delta * eased);
            if (t < 1) raf = requestAnimationFrame(tick);
            else setN(to);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [started, to, duration]);

    const formatted = decimals
        ? n.toFixed(decimals)
        : Math.round(n).toLocaleString("de-DE");

    return (
        <span ref={ref} className={className}>
            {prefix}{formatted}{suffix}
        </span>
    );
}

/* ------------------------------------------------------------------ */
/* MagneticButton — subtle magnetic follow on hover                    */
/* ------------------------------------------------------------------ */

export function MagneticButton({ as: Tag = "a", strength = 18, className = "", children, ...rest }) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Skip magnetism on touch devices / reduced motion
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        if (window.matchMedia("(pointer: coarse)").matches) return;

        let raf;
        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            const x = e.clientX - rect.left - rect.width / 2;
            const y = e.clientY - rect.top - rect.height / 2;
            const dx = (x / rect.width) * strength;
            const dy = (y / rect.height) * strength;
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                el.style.transform = `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`;
            });
        };
        const onLeave = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                el.style.transform = "translate(0, 0)";
            });
        };
        el.addEventListener("mousemove", onMove);
        el.addEventListener("mouseleave", onLeave);
        return () => {
            el.removeEventListener("mousemove", onMove);
            el.removeEventListener("mouseleave", onLeave);
            cancelAnimationFrame(raf);
        };
    }, [strength]);

    return (
        <Tag ref={ref} className={`bm-magnetic ${className}`} {...rest}>
            {children}
        </Tag>
    );
}

/* ------------------------------------------------------------------ */
/* useParallax — returns transform style for layered hero parallax     */
/* ------------------------------------------------------------------ */

export function useMouseParallax(strength = 12) {
    const [pos, setPos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        if (window.matchMedia("(pointer: coarse)").matches) return;
        let raf;
        const onMove = (e) => {
            const cx = window.innerWidth / 2;
            const cy = window.innerHeight / 2;
            const dx = ((e.clientX - cx) / cx) * strength;
            const dy = ((e.clientY - cy) / cy) * strength;
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => setPos({ x: dx, y: dy }));
        };
        window.addEventListener("mousemove", onMove);
        return () => {
            window.removeEventListener("mousemove", onMove);
            cancelAnimationFrame(raf);
        };
    }, [strength]);

    return pos;
}

/* ------------------------------------------------------------------ */
/* Skeleton — dark shimmer placeholder                                 */
/* ------------------------------------------------------------------ */

export function Skeleton({ className = "", style }) {
    return <div className={`bm-skeleton ${className}`} style={style} aria-hidden="true" />;
}
